import React, { useMemo } from "react";
import { connect, useSelector } from "react-redux";
import SideNav from "components/layout-components/SideNav";
import TopNav from "components/layout-components/TopNav";
import MobileNav from "components/layout-components/MobileNav";
import HeaderNav from "components/layout-components/HeaderNav";
import { PageCrumbProvider } from "../../data/hooks/usePageCrumb";
import PageHeader from "components/layout-components/PageHeader";
import Footer from "components/layout-components/Footer";
import AppViews from "views/app-views";
import { Grid, Layout } from "antd";

import navigationConfig from "configs/NavigationConfig";
import { NAV_TYPE_SIDE, NAV_TYPE_TOP } from "constants/ThemeConstant";
import utils from "utils";
import { useFirestoreConnect } from "react-redux-firebase";
import {
  FIRESTORE_CATEGORIES_TABLE,
  FIRESTORE_CMS_ROLES_TABLE,
  FIRESTORE_ORDERS_TABLE,
  FIRESTORE_PRODUCTS_TABLE,
} from "../../constants/FirestoreConstant";
import * as dayjs from "dayjs";
import { usePermissions } from "../../data/hooks/usePermissions";
import { useDataHealth } from "../../data/hooks/useDataHealth";
import { PERMISSIONS } from "../../functions/permissions";

const { Content } = Layout;
const { useBreakpoint } = Grid;

export const AppLayout = ({ navCollapsed, navType, location, direction }) => {
  const queryModifications = useSelector((state) => state.queries);
  const { can, loading: permissionsLoading } = usePermissions();

  // Welke collecties naar de browser gaan hangt af van wat de gebruiker mag.
  // Tot BMX-35 laadde iedereen alles (alle orders, inkoop, dealers, ...). Nu
  // alleen wat de eigen permissies nodig hebben.
  //
  // Hier horen alléén collecties die het hele CMS door gelezen worden. Pagina-
  // specifieke collecties (carts, purchaseOrders, dealers, promotions,
  // discountCodes, groups, collections, featuredProducts, salesPersons) zijn
  // verhuisd naar de pagina's die ze lezen: ze stonden hier op élke CMS-pagina
  // geabonneerd, terwijl maar één scherm ze gebruikte. Bij een realtime
  // listener kost dat niet alleen geheugen — elke schrijfactie in zo'n
  // collectie was overal een Redux-action. `carts` was daarin de ergste: die
  // groeit mee met elke webshopbezoeker en wordt continu geschreven.
  // Zie docs/cms-performance.md; voeg hier dus niets toe wat één scherm nodig
  // heeft.
  //
  // De landfiltering zelf zit niet hier maar in useScopedCustomers /
  // useScopedOrders: een Firestore `in`-query op land naast de bestaande
  // filters zou een samengestelde index vereisen die we vanuit de repo niet
  // kunnen aanmaken.
  const allQueries = useMemo(() => {
    // De rollen (cmsRoles) altijd, ook zolang de permissies nog laden: daar
    // wachten de permissies juist op. Beheer → Rollen schrijft erin.
    const roleQuery = { collection: FIRESTORE_CMS_ROLES_TABLE, storeAs: "cmsRoles" };
    if (permissionsLoading) return [roleQuery];
    const queries = [
      roleQuery,
      // Producten: gelezen door ~20 schermen, tot in de orderdetails aan toe.
      { collection: FIRESTORE_PRODUCTS_TABLE },
    ];
    if (
      can(PERMISSIONS.PRODUCTS_MANAGE) ||
      can(PERMISSIONS.CATEGORIES_MANAGE) ||
      can(PERMISSIONS.PURCHASING_MANAGE) ||
      can(PERMISSIONS.REPORTS_VIEW)
    ) {
      queries.push({ collection: FIRESTORE_CATEGORIES_TABLE, orderBy: ["position", "asc"] });
    }
    if (can(PERMISSIONS.ORDERS_VIEW)) {
      // Standaard de laatste N dagen (state.queries.ordersMaxDaysAgo); de
      // orderlijst kan dat opheffen met "alle orders ophalen".
      if (!!queryModifications?.ordersMaxDaysAgo) {
        const orderDaysAgoTimestamp = dayjs().subtract(queryModifications.ordersMaxDaysAgo, "days").unix();
        queries.push({ collection: FIRESTORE_ORDERS_TABLE, where: ["createdAt", ">=", orderDaysAgoTimestamp] });
      } else {
        queries.push({ collection: FIRESTORE_ORDERS_TABLE });
      }
    }
    return queries;
  }, [can, permissionsLoading, queryModifications]);

  // Fetch all collections from Firestore and put in redux store
  useFirestoreConnect(allQueries);
  // Meldt mislukte reads en lijsten die leeg blijven terwijl dat niet hoort.
  useDataHealth();
  const currentRouteInfo = utils.getRouteInfo(navigationConfig, location.pathname);
  const screens = utils.getBreakPoint(useBreakpoint());
  const isMobile = !screens.includes("lg");
  const isNavSide = navType === NAV_TYPE_SIDE;
  const isNavTop = navType === NAV_TYPE_TOP;

  return (
    // Eén provider om header en pagina's: HeaderNav leest het kruimellabel dat
    // de actieve detailpagina publiceert.
    <PageCrumbProvider>
      <Layout style={{ height: "100vh" }}>
        <HeaderNav isMobile={isMobile} />
        {isNavTop && !isMobile ? <TopNav /> : null}
        <Layout className="app-container" style={{ flex: 1, overflow: "hidden" }}>
          {isNavSide && !isMobile ? <SideNav /> : null}
          <Layout className="app-layout" style={{ paddingLeft: 0, flex: 1, display: "flex", flexDirection: "column" }}>
            <div className={`app-content ${isNavTop ? "layout-top-nav" : ""}`} style={{ flex: 1, overflow: "auto" }}>
              <PageHeader display={currentRouteInfo?.breadcrumb} title={currentRouteInfo?.title} />
              <Content style={{ height: "100%" }}>
                <AppViews />
              </Content>
            </div>
            <Footer />
          </Layout>
        </Layout>
        {isMobile && <MobileNav />}
      </Layout>
    </PageCrumbProvider>
  );
};

const mapStateToProps = ({ theme }) => {
  const { navCollapsed, navType, locale } = theme;
  return { navCollapsed, navType, locale };
};

export default connect(mapStateToProps)(React.memo(AppLayout));
